import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ArrowLeft, Copy, Check, Wallet, TrendingUp, Activity, AlertCircle } from 'lucide-react';
import Web3 from 'web3';

const Container = styled.div`
  background: linear-gradient(135deg, #020611 0%, #050B15 50%, #020611 100%);
  min-height: 100vh;
  color: #ffffff;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const Header = styled.header`
  padding: 1rem 1rem;
  background: rgba(11, 20, 38, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
  
  @media (min-width: 768px) {
    padding: 1.5rem 2rem;
  }
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  @media (min-width: 768px) {
    gap: 1rem;
  }
`;

const BackButton = styled.button`
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  color: #3B82F6;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  font-size: 0.875rem;
  
  &:hover {
    background: rgba(59, 130, 246, 0.2);
    border-color: rgba(59, 130, 246, 0.5);
  }
  
  @media (min-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 1rem;
  }
`;

const Title = styled.h1`
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0;
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 1rem auto;
  padding: 0 1rem;
  
  @media (min-width: 768px) {
    margin: 1rem auto 0 auto;
    padding: 0 2rem;
  }
`;

const OverviewCard = styled.div`
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
  backdrop-filter: blur(10px);
  
  @media (min-width: 768px) {
    border-radius: 16px;
    padding: 2rem;
    margin-bottom: 2rem;
  }
`;

const AddressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    margin-bottom: 2rem;
  }
`;

const AddressIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #3B82F6 0%, #06B6D4 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  
  @media (min-width: 768px) {
    width: 56px;
    height: 56px;
  }
`;

const AddressInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const AddressLabel = styled.div`
  color: #94A3B8;
  font-size: 0.8125rem;
  margin-bottom: 0.25rem;
  
  @media (min-width: 768px) {
    font-size: 0.875rem;
  }
`;

const AddressText = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  word-break: break-all;
  line-height: 1.4;
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const CopyButton = styled.button`
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  color: #3B82F6;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  font-size: 0.875rem;
  width: 100%;
  
  &:hover {
    background: rgba(59, 130, 246, 0.2);
  }
  
  @media (min-width: 768px) {
    width: auto;
    font-size: 1rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  
  @media (min-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  
  @media (min-width: 768px) {
    gap: 0.5rem;
  }
`;

const StatLabel = styled.div`
  color: #94A3B8;
  font-size: 0.8125rem;
  font-weight: 500;
  
  @media (min-width: 768px) {
    font-size: 0.875rem;
  }
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  
  @media (min-width: 768px) {
    font-size: 1.75rem;
  }
`;

const StatSubtext = styled.div`
  color: #64748B;
  font-size: 0.75rem;
  
  @media (min-width: 768px) {
    font-size: 0.875rem;
  }
`;

const Section = styled.div`
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.25rem;
  backdrop-filter: blur(10px);
  
  @media (min-width: 768px) {
    border-radius: 16px;
    padding: 2rem;
    margin-bottom: 1rem;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
  
  @media (min-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
    gap: 0.75rem;
  }
`;

const SectionIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.iconColor};
  flex-shrink: 0;
  
  @media (min-width: 768px) {
    width: 32px;
    height: 32px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: #94A3B8;
  gap: 1rem;
  
  @media (min-width: 768px) {
    padding: 4rem 2rem;
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: #EF4444;
  gap: 1rem;
  
  @media (min-width: 768px) {
    padding: 4rem 2rem;
  }
`;

const ErrorMessage = styled.div`
  font-size: 1rem;
  text-align: center;
  
  @media (min-width: 768px) {
    font-size: 1.125rem;
  }
`;

const RetryButton = styled.button`
  background: #3B82F6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.3s ease;
  font-size: 0.875rem;
  
  &:hover {
    background: #2563EB;
  }
  
  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const TokenList = styled.div`
  display: grid;
  gap: 0.875rem;
  
  @media (min-width: 768px) {
    gap: 1rem;
  }
`;

const TokenItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  transition: all 0.3s ease;
  gap: 0.75rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(59, 130, 246, 0.3);
  }
  
  @media (min-width: 768px) {
    padding: 1.25rem;
    gap: 1rem;
  }
`;

const TokenInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
  
  @media (min-width: 768px) {
    gap: 1rem;
  }
`;

const TokenIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3B82F6 0%, #06B6D4 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 1rem;
  flex-shrink: 0;
  
  @media (min-width: 768px) {
    width: 48px;
    height: 48px;
    font-size: 1.125rem;
  }
`;

const TokenDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
  
  @media (min-width: 768px) {
    gap: 0.25rem;
  }
`;

const TokenName = styled.div`
  font-weight: 600;
  color: #ffffff;
  font-size: 0.9375rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  
  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const TokenSymbol = styled.div`
  color: #94A3B8;
  font-size: 0.8125rem;
  
  @media (min-width: 768px) {
    font-size: 0.875rem;
  }
`;

const TokenAddress = styled.div`
  color: #64748B;
  font-size: 0.6875rem;
  font-family: 'Courier New', monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  
  @media (min-width: 768px) {
    font-size: 0.75rem;
  }
`;

const TokenBalance = styled.div`
  text-align: right;
  flex-shrink: 0;
`;

const TokenAmount = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #ffffff;
  white-space: nowrap;
  
  @media (min-width: 768px) {
    font-size: 1.125rem;
  }
`;

const InfoBox = styled.div`
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 12px;
  padding: 0.875rem;
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  margin-top: 1rem;
  
  @media (min-width: 768px) {
    padding: 1rem;
    align-items: center;
    gap: 0.75rem;
  }
`;

const InfoText = styled.div`
  color: #94A3B8;
  font-size: 0.8125rem;
  line-height: 1.5;
  
  @media (min-width: 768px) {
    font-size: 0.875rem;
  }
`;

const InfoIconWrapper = styled.div`
  flex-shrink: 0;
  margin-top: 0.125rem;
  
  @media (min-width: 768px) {
    margin-top: 0;
  }
`;

// Standard ERC-20 ABI for balanceOf, name, symbol, decimals
const ERC20_ABI = [
  {
    constant: true,
    inputs: [{ name: "_owner", type: "address" }],
    name: "balanceOf",
    outputs: [{ name: "balance", type: "uint256" }],
    type: "function",
  },
  {
    constant: true,
    inputs: [],
    name: "name",
    outputs: [{ name: "", type: "string" }],
    type: "function",
  },
  {
    constant: true,
    inputs: [],
    name: "symbol",
    outputs: [{ name: "", type: "string" }],
    type: "function",
  },
  {
    constant: true,
    inputs: [],
    name: "decimals",
    outputs: [{ name: "", type: "uint8" }],
    type: "function",
  },
];

const WalletDetails = ({ address, onBack }) => {
  const [walletData, setWalletData] = useState({
    balance: '0',
    transactionCount: 0,
    tokens: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const web3 = new Web3('https://chainfree.info:5010/validator/3');

  // Add your known ERC-20 token contract addresses here
  const KNOWN_TOKENS = [
    '0xfa5e9E839C25B1Ef876177Ba3D3DD2d27c1ede95',
    '0xc4d635D00F4d3e731B1C99ae0DFe662fdc994334'
    // Add token addresses as you discover them on your chain
  ];

  useEffect(() => {
    fetchWalletData();
  }, [address]);

  const fetchWalletData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔍 Fetching wallet data for:', address);
      
      // Fetch native balance (HYX)
      const balance = await web3.eth.getBalance(address);
      const balanceInEther = Web3.utils.fromWei(balance, 'ether');
      console.log('✅ Balance:', balanceInEther, 'HYX');

      // Fetch transaction count
      const txCount = await web3.eth.getTransactionCount(address);
      console.log('✅ Transaction count:', txCount);

      // Start with native token
      const tokens = [
        {
          name: 'Hydrix',
          symbol: 'HYX',
          balance: balanceInEther,
          isNative: true,
          address: 'Native Token'
        }
      ];

      // Fetch ERC-20 token balances
      if (KNOWN_TOKENS.length > 0) {
        console.log('🔍 Checking ERC-20 tokens...');
        for (const tokenAddress of KNOWN_TOKENS) {
          try {
            const tokenData = await fetchTokenBalance(tokenAddress, address);
            if (tokenData && parseFloat(tokenData.balance) > 0) {
              tokens.push(tokenData);
              console.log('✅ Found token:', tokenData.symbol, tokenData.balance);
            }
          } catch (err) {
            console.warn('⚠️ Error fetching token', tokenAddress, err.message);
          }
        }
      }

      setWalletData({
        balance: balanceInEther,
        transactionCount: txCount,
        tokens
      });
      
      console.log('✅ Wallet data loaded');
      
    } catch (error) {
      console.error('❌ Error fetching wallet data:', error);
      setError(error.message || 'Failed to load wallet data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTokenBalance = async (tokenAddress, walletAddress) => {
    try {
      const contract = new web3.eth.Contract(ERC20_ABI, tokenAddress);
      
      // Fetch token details
      const [name, symbol, decimals, rawBalance] = await Promise.all([
        contract.methods.name().call(),
        contract.methods.symbol().call(),
        contract.methods.decimals().call(),
        contract.methods.balanceOf(walletAddress).call()
      ]);

      // Convert balance using decimals
      const balance = (Number(rawBalance) / Math.pow(10, Number(decimals))).toString();

      return {
        name,
        symbol,
        balance,
        decimals: Number(decimals),
        address: tokenAddress,
        isNative: false
      };
    } catch (error) {
      console.error('Error fetching token data for', tokenAddress, error);
      return null;
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (error) {
    return (
      <Container>
        <Header>
          <HeaderContent>
            <BackButton onClick={onBack}>
              <ArrowLeft size={16} />
              <span>Back</span>
            </BackButton>
            <Title>Wallet Details</Title>
          </HeaderContent>
        </Header>
        <ErrorContainer>
          <Activity size={40} color="#EF4444" />
          <ErrorMessage>Error loading wallet data</ErrorMessage>
          <div style={{ color: '#94A3B8', fontSize: '0.875rem', textAlign: 'center', padding: '0 1rem' }}>{error}</div>
          <RetryButton onClick={fetchWalletData}>Retry</RetryButton>
        </ErrorContainer>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <Header>
          <HeaderContent>
            <BackButton onClick={onBack}>
              <ArrowLeft size={16} />
              <span>Back</span>
            </BackButton>
            <Title>Wallet Details</Title>
          </HeaderContent>
        </Header>
        <LoadingContainer>
          <Activity size={28} style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ fontSize: '1rem' }}>Loading wallet data...</span>
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderContent>
          <BackButton onClick={onBack}>
            <ArrowLeft size={16} />
            <span>Back</span>
          </BackButton>
          <Title>Wallet Details</Title>
        </HeaderContent>
      </Header>

      <Content>
        <OverviewCard>
          <AddressContainer>
            <AddressIcon>
              <Wallet size={24} />
            </AddressIcon>
            <AddressInfo>
              <AddressLabel>Wallet Address</AddressLabel>
              <AddressText>{address}</AddressText>
            </AddressInfo>
            <CopyButton onClick={handleCopy}>
              {copied ? <Check size={16} /> : <Copy size={16} />}
              <span>{copied ? 'Copied!' : 'Copy'}</span>
            </CopyButton>
          </AddressContainer>

          <StatsGrid>
            <StatItem>
              <StatLabel>HYX Balance</StatLabel>
              <StatValue>{parseFloat(walletData.balance).toFixed(4)} HYX</StatValue>
              <StatSubtext>≈ $0.00 USD</StatSubtext>
            </StatItem>
            <StatItem>
              <StatLabel>Total Transactions</StatLabel>
              <StatValue>{walletData.transactionCount}</StatValue>
              <StatSubtext>Nonce count</StatSubtext>
            </StatItem>
            <StatItem>
              <StatLabel>Token Types</StatLabel>
              <StatValue>{walletData.tokens.length}</StatValue>
              <StatSubtext>Different assets</StatSubtext>
            </StatItem>
          </StatsGrid>
        </OverviewCard>

        <Section>
          <SectionHeader>
            <SectionTitle>
              <SectionIcon color="rgba(6, 182, 212, 0.2)" iconColor="#06B6D4">
                <TrendingUp size={16} />
              </SectionIcon>
              <span>Token Holdings</span>
            </SectionTitle>
          </SectionHeader>
          
          <TokenList>
            {walletData.tokens.map((token, index) => (
              <TokenItem key={index}>
                <TokenInfo>
                  <TokenIcon>
                    {token.symbol.charAt(0)}
                  </TokenIcon>
                  <TokenDetails>
                    <TokenName>{token.name}</TokenName>
                    <TokenSymbol>{token.symbol}</TokenSymbol>
                    {!token.isNative && (
                      <TokenAddress>
                        {token.address.slice(0, 6)}...{token.address.slice(-4)}
                      </TokenAddress>
                    )}
                  </TokenDetails>
                </TokenInfo>
                <TokenBalance>
                  <TokenAmount>{parseFloat(token.balance).toFixed(4)}</TokenAmount>
                  <TokenSymbol>{token.symbol}</TokenSymbol>
                </TokenBalance>
              </TokenItem>
            ))}
          </TokenList>

          {KNOWN_TOKENS.length === 0 && (
            <InfoBox>
              <InfoIconWrapper>
                <AlertCircle size={18} color="#3B82F6" />
              </InfoIconWrapper>
              <InfoText>
                To display ERC-20 tokens, add token contract addresses to the KNOWN_TOKENS array in WalletDetails.js
              </InfoText>
            </InfoBox>
          )}
        </Section>
      </Content>
    </Container>
  );
};

export default WalletDetails;